const ALLOWED_TAGS = new Set([
  'br',
  'div',
  'p',
  'figure',
  'img',
  'ul',
  'ol',
  'li',
  'strong',
  'em',
  'b',
  'i',
  'u',
  'span',
  'blockquote',
  'a',
  'font',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'hr',
  's',
  'strike',
  'mark',
  'small',
  'code',
  'pre',
  'sub',
  'sup',
  'center',
]);

/** style 속성 내 일반적인 XSS 패턴 차단 (완화된 화이트리스트 대신) */
const DANGEROUS_STYLE_PATTERN =
  /(url\s*\(|expression\s*\(|javascript\s*:|@import|@-moz-binding|-moz-binding|behavior\s*:|\\0|<\/|<!--|<script)/i;

function isSafeInlineStyle(value: string): boolean {
  if (!value || value.length > 8000) return false;
  return !DANGEROUS_STYLE_PATTERN.test(value);
}

function isSafeHref(value: string): boolean {
  const v = value.trim().toLowerCase();
  if (!v) return false;
  if (v.startsWith('javascript:') || v.startsWith('vbscript:')) return false;
  if (v.startsWith('data:')) return false;
  return true;
}

function keepAttribute(tag: string, attr: string, value: string): boolean {
  const a = attr.toLowerCase();

  if (a === 'class') return true;
  if (a === 'style') return isSafeInlineStyle(value);

  if (tag === 'img') {
    if (a === 'src') return !value.trim().toLowerCase().startsWith('javascript:');
    if (a === 'alt' || a === 'data-embed' || a === 'width' || a === 'height' || a === 'loading' || a === 'decoding') {
      return true;
    }
    return false;
  }

  if (tag === 'font') {
    return a === 'color' || a === 'face' || a === 'size';
  }

  if (tag === 'figure') {
    return a === 'data-embed' || a === 'contenteditable';
  }

  if (tag === 'a') {
    if (a === 'href') return isSafeHref(value);
    if (a === 'target') {
      return /^(_blank|_self|_parent|_top)$/i.test(value.trim());
    }
    if (a === 'rel' || a === 'title') return true;
    return false;
  }

  if (
    a === 'align' &&
    ['div', 'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'hr', 'center'].includes(tag)
  ) {
    return /^(left|right|center|justify)$/i.test(value.trim());
  }

  return false;
}

const removeNodeKeepChildren = (element: Element) => {
  const parent = element.parentNode;
  if (!parent) return;
  while (element.firstChild) {
    parent.insertBefore(element.firstChild, element);
  }
  parent.removeChild(element);
};

const sanitizeAllowedAttributes = (element: Element) => {
  const tag = element.tagName.toLowerCase();
  const attrs = Array.from(element.attributes);

  attrs.forEach(({ name, value }) => {
    const attr = name.toLowerCase();
    if (!keepAttribute(tag, attr, value)) {
      element.removeAttribute(name);
    }
  });
};

export const sanitizeProjectDescHtml = (html: string): string => {
  if (!html) return html;

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  doc.querySelectorAll('script, style, iframe, object, embed, link, meta').forEach((node) => node.remove());

  Array.from(doc.body.querySelectorAll('*')).forEach((element) => {
    const tag = element.tagName.toLowerCase();
    if (!ALLOWED_TAGS.has(tag)) {
      removeNodeKeepChildren(element);
      return;
    }
    sanitizeAllowedAttributes(element);
  });

  return doc.body.innerHTML;
};

const STORY_FIGURE_TOKEN_PREFIX = '__MF_STORY_FIGURE__';
const STORY_BR_TOKEN_PREFIX = '__MF_STORY_BR__';

const escapeHtmlForEditor = (value: string): string => {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
};

export const hasLegacyStoryMarkup = (html: string): boolean => {
  if (!html) return false;

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  return Array.from(doc.body.querySelectorAll('*')).some((element) => {
    const tag = element.tagName.toLowerCase();

    if (tag === 'br') return false;
    if (tag === 'figure' && element.getAttribute('data-embed') === 'story-image') return false;
    if (tag === 'img' && element.closest('figure[data-embed="story-image"]')) return false;

    return true;
  });
};

export const prepareStoryHtmlForEditor = (html: string): string => {
  if (!html) return '';

  const figures: string[] = [];
  const brs: string[] = [];

  let working = html.replace(
    /<figure\b[^>]*data-embed=(["'])story-image\1[^>]*>[\s\S]*?<\/figure>/gi,
    (match) => {
      const index = figures.push(match) - 1;
      return `${STORY_FIGURE_TOKEN_PREFIX}${index}__`;
    },
  );

  working = working.replace(/<br\s*\/?>/gi, () => {
    const index = brs.push('<br>') - 1;
    return `${STORY_BR_TOKEN_PREFIX}${index}__`;
  });

  working = escapeHtmlForEditor(working);

  working = working.replace(
    new RegExp(`${STORY_FIGURE_TOKEN_PREFIX}(\\d+)__`, 'g'),
    (_, index) => figures[Number(index)] ?? '',
  );
  working = working.replace(
    new RegExp(`${STORY_BR_TOKEN_PREFIX}(\\d+)__`, 'g'),
    (_, index) => brs[Number(index)] ?? '<br>',
  );

  return working;
};

export const restoreStoryHtmlFromEditor = (html: string): string => {
  if (!html) return '';

  return html
    .replace(/&nbsp;/g, ' ')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, '\'')
    .replace(/&amp;/g, '&');
};

const decodeEscapedHtmlOnce = (html: string): string => {
  return html
    .replace(/&nbsp;/gi, ' ')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, '\'')
    .replace(/&amp;/gi, '&');
};

export const normalizeStoryHtmlForDisplay = (html: string): string => {
  if (!html) return '';

  let normalized = html;

  // Older posts can be stored with HTML escaped multiple times.
  for (let i = 0; i < 3; i += 1) {
    const decoded = decodeEscapedHtmlOnce(normalized);
    if (decoded === normalized) break;
    normalized = decoded;
  }

  normalized = normalized
    .replace(/<div\b[^>]*>\s*<br\s*\/?>\s*<\/div>/gi, '<br>')
    .replace(/<p\b[^>]*>\s*<br\s*\/?>\s*<\/p>/gi, '<br>')
    .replace(/<\/(div|p)>/gi, '<br>')
    .replace(/<(div|p)\b[^>]*>/gi, '')
    .replace(/(<br\s*\/?>\s*){3,}/gi, '<br><br>');

  return sanitizeProjectDescHtml(normalized);
};
